import { blackListedStudypackIdsForWeb, isMocked } from "@constants/common";
import { useAppStore } from "@hooks/useAppStore";
import { formatSecondsToUTCtime } from "@memorang/helpers/src/common";
import type { Distribution } from "@memorang/types/src/content";
import type { ExamDetails } from "@memorang/types/src/exams";
import type { PracticeTestExam } from "@memorang/types/src/tag";
import { formatTimeString } from "./date";
import { isWeb } from "./platform";

export const getIsOlxExam = (id: string) => id.includes("OLX");

const getDescAndCTA = ({
	status,
	numItems,
	totalTime,
	dateCompleted,
	timeElapsed,
	attempted,
	endStartDate,
}: PracticeTestExam) => {
	if (status === "COMPLETE") {
		return {
			description: `${numItems} items • ${formatTimeString(dateCompleted)}`,
			cta: "View report",
		};
	}
	if (status === "ACTIVE") {
		const timeRemaining = totalTime - timeElapsed;
		const numAttempts = attempted
			? attempted.high + attempted.low + attempted.medium
			: 0;
		return {
			description: `${numAttempts} / ${numItems} items • ${formatSecondsToUTCtime(
				timeRemaining,
			)} Remaining${
				endStartDate
					? ` • Ends on: ${formatTimeString(endStartDate, true, false)}`
					: ""
			}`,
			cta: "Resume",
		};
	}
	if (status === "NOT_STARTED") {
		return {
			description: `${numItems} items • ${formatSecondsToUTCtime(totalTime)}${
				endStartDate
					? ` • Ends on: ${formatTimeString(endStartDate, true, false)}`
					: ""
			}`,
			cta: "Start",
		};
	}
	return {
		description: `${numItems} items • ${formatSecondsToUTCtime(totalTime)}`,
		cta: "Start",
	};
};
export const calculateScore = (distribution: Distribution) => {
	const { high, low } = distribution;

	const itemsScore = high;
	const total = high + low;
	if (total === 0) {
		return 0;
	}

	const score = itemsScore / total;
	return Math.round(score * 100);
};
export const getMappedExams = (exams: PracticeTestExam[]) => {
	const mappedExams = exams.map((exam) => {
		const { title, status, id, numericId, distribution, collectionId } = exam;
		const { description, cta } = getDescAndCTA(exam);
		const score = distribution ? calculateScore(distribution) : 0;
		return {
			title,
			description,
			collectionId,
			status: status || ("NOT_STARTED" as const),
			numericId: numericId || 0,
			cta,
			id,
			score,
			hasExamAccess: isMocked || exam.hasAccess,
			type: exam.type!,
			subType: exam.subType!,
			beginStartDate: exam.beginStartDate,
			endStartDate: exam.endStartDate,
		};
	});

	const legacyReports = exams.filter((item) => item.legacyReport != null);
	const migratedReports = exams.filter(
		(item) => item.migratedSessionReport != null,
	);

	if (legacyReports.length > 0) {
		const legacyReport = legacyReports.map((item) => {
			const { title, legacyReport } = item;
			const { description, cta } = getDescAndCTA({
				...item,
				status: "COMPLETE",
				...legacyReport!,
			});
			const { numericId, id, score } = legacyReport!;

			return {
				title,
				description,
				status: "LEGACYREPORT" as const,
				numericId: numericId || 0,
				cta,
				id,
				collectionId: item.collectionId,
				score: Math.round(score),
				hasExamAccess: true,
				subType: item.subType!,
				type: item.type!,
				beginStartDate: item.beginStartDate,
				endStartDate: item.endStartDate,
				legacyReport: item.legacyReport,
				migratedSessionReport: item.migratedSessionReport,
			};
		});
		mappedExams.push(...legacyReport);
	}

	if (migratedReports.length > 0) {
		const migratedReport = migratedReports.map((item) => {
			const { title, migratedSessionReport } = item;
			const { description, cta } = getDescAndCTA({
				...item,
				status: "COMPLETE",
				...migratedSessionReport!,
			});
			const { numericId, id, score } = migratedSessionReport!;
			return {
				title,
				description,
				status: "MIGRATEDREPORT" as const,
				numericId: numericId || 0,
				cta,
				id,
				collectionId: item.collectionId,
				score: Math.round(score),
				hasExamAccess: true,
				type: item.type!,
				subType: item.subType!,
				beginStartDate: item.beginStartDate,
				endStartDate: item.endStartDate,
				legacyReport: item.legacyReport,
				migratedSessionReport: item.migratedSessionReport,
			};
		});
		mappedExams.push(...migratedReport);
	}

	return mappedExams;
};

export const getShowStudyPackViews = (
	studyPackProductCollectionId?: number,
) => {
	const isStudyPackEnabled = useAppStore.getState().app?.isStudyPackEnabled;

	if (!isWeb && isStudyPackEnabled) {
		return true;
	}
	if (!studyPackProductCollectionId) {
		return false;
	}

	const result = !blackListedStudypackIdsForWeb.includes(
		studyPackProductCollectionId,
	);

	return result;
};

export const isOlxExamWithinDateRange = (
	beginStartDate?: string,
	endStartDate?: string,
): boolean => {
	if (!(beginStartDate && endStartDate)) {
		return true;
	}

	const beginTime = new Date(beginStartDate).getTime();
	const endTime = new Date(endStartDate).getTime();
	const currentTime = Date.now();

	return currentTime >= beginTime && currentTime <= endTime;
};

export const getOLXExamEligibilityConfig = (
	startDate: string,
	endDate: string,
) => {
	const currentTime = Date.now();
	const beginTime = new Date(startDate).getTime();
	const endTime = new Date(endDate).getTime();

	if (currentTime < beginTime) {
		return {
			title: "Exam Not Yet Available",
			body: `You have unlocked the OLX exam, but you cannot start it yet. The exam will be available on ${formatTimeString(
				startDate,
				true,
				false,
			)}. Please check back then.`,
		};
	}
	if (currentTime > endTime) {
		return {
			title: "Exam Window Closed",
			body: "Unfortunately, the window to take your OLX exam has passed. Please contact your professor or school administrator for further assistance.",
		};
	}
	return {
		title: "Start Your OLX Exam",
		body: `You have unlocked the OLX exam. You can start the exam now and continue until ${formatTimeString(
			endDate,
			true,
			false,
		)} from the practice tests tab.`,
		cta: "Go to Practice tests",
	};
};

export const groupExamsByCategory = (
	examList: ExamDetails[],
): {
	title: string;
	icon: string;
	data: ExamDetails[];
}[] => {
	const groupedData = examList.reduce<Record<string, ExamDetails[]>>(
		(acc, cur) => {
			const category = cur.exam.category[0].name;

			if (!acc[category]) {
				acc[category] = [];
			}

			const filteredCur = {
				...cur,
				exam: {
					...cur.exam,
					bundles: cur.exam.bundles
						.slice()
						.sort((a, b) => a.bundle.numericId - b.bundle.numericId),
				},
			};

			acc[category].push(filteredCur);

			return acc;
		},
		{},
	);

	const output = Object.keys(groupedData).map((category) => ({
		title: category,
		icon: groupedData[category][0].exam.category[0].icon,
		data: groupedData[category],
	}));

	return output;
};
